import React, { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, IconButton, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { AuthContext } from '../../../context/logeduser';
import { useMyForm16List, form16PdfUrl } from '../../../hooks/useForm16';
import PageHeader from '../PageHeader';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

const outlinedBtn = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };

export default function Form16Emp() {
  const { authData } = useContext(AuthContext);
  const employeeNo = authData?.employee_no;
  const { data: list = [], isLoading } = useMyForm16List(employeeNo);

  return (
    <Box sx={{ pb: { xs: 4, md: 2 } }}>
      {/* ---- Header card (shared PageHeader) ---- */}
      <PageHeader
        icon={<ArticleIcon />}
        title="My Form 16"
        subtitle="Use these certificates while filing your Income Tax Return. Reconcile against Form 26AS / AIS on incometax.gov.in."
        action={(
          <Stack direction="row" spacing={1}>
            <Button component={RouterLink} to="/declarations" variant="outlined" sx={outlinedBtn}>
              My Declarations
            </Button>
            <Button component={RouterLink} to="/form16/rectify" variant="outlined" sx={outlinedBtn}>
              Raise Rectification
            </Button>
          </Stack>
        )}
      />

      {/* ---- Certificates table ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
        {!isLoading && list.length === 0 ? (
          <Box sx={{ py: 7, textAlign: 'center' }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
              <EventBusyIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No Form 16 has been issued to you yet.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['FY', 'Period', 'Certificate No.'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', borderColor: '#eef0f4' }}>{h}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#374151', borderColor: '#eef0f4' }}>Total Salary</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#374151', borderColor: '#eef0f4' }}>Total TDS</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151', borderColor: '#eef0f4' }}>Issued On</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#374151', borderColor: '#eef0f4' }}>Download</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => (
                  <TableRow key={row.id} hover sx={{ '& td': { borderColor: '#f1f5f9' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.fy_label}</TableCell>
                    <TableCell>{row.period_from} → {row.period_to}</TableCell>
                    <TableCell>{row.certificate_no || '—'}</TableCell>
                    <TableCell align="right">{Number(row.total_salary).toLocaleString('en-IN')}</TableCell>
                    <TableCell align="right">{Number(row.total_tds).toLocaleString('en-IN')}</TableCell>
                    <TableCell>{row.published_at?.slice(0, 10) || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View">
                        <IconButton size="small" component={RouterLink} to={`/form16/${row.id}`} sx={{ color: ACCENT }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Part A">
                        <IconButton size="small" component="a" href={form16PdfUrl(row.id, 'part-a')} target="_blank" sx={{ color: '#6b7280' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Part B">
                        <IconButton size="small" component="a" href={form16PdfUrl(row.id, 'part-b')} target="_blank" sx={{ color: '#6b7280' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Combined">
                        <IconButton size="small" component="a" href={form16PdfUrl(row.id, 'combined')} target="_blank" sx={{ color: ACCENT }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
